import { API_URL } from "@/config/constants";
import { parseContentDispositionFilename } from "@/utils/download-blob";
import axios from "axios";
import { isAxiosNotFoundError } from "../errors";
import {
    IDocumentCustomFieldCreate,
    IDocumentCustomFieldPropertyCreate,
    IDocumentCustomFieldUpdate,
    IDocumentExtractedPropertyUpdate,
    ICustomFieldPropertyResponse,
    ICustomFieldResponse,
    IDocumentExtractedFieldsResponse,
    DocumentInfoVariant,
    IDocumentInfo,
    IDocumentInfoOptions,
    IExtractedCOIResponse,
    IExtractedCOIUpdate,
    IExtractedPropertyHighlightAreas,
    IExtractedPropertyInDB,
    IExtractionTriggerResponse,
} from "./documents.types";

const API = `${API_URL}/documents`;
const storageAxios = axios.create({
    headers: {},
});
delete storageAxios.defaults.headers.common["Authorization"];

export class OutputDocumentMetadataNotFoundError extends Error {
    constructor(public readonly documentId: number) {
        super("The document no longer exists");
        this.name = "OutputDocumentMetadataNotFoundError";
    }
}

export function isOutputDocumentMetadataNotFoundError(
    error: unknown
): error is OutputDocumentMetadataNotFoundError {
    return error instanceof OutputDocumentMetadataNotFoundError;
}

class OutputDocumentUnavailableError extends Error {
    constructor() {
        super("The document is not currently available for download");
        this.name = "OutputDocumentUnavailableError";
    }
}

export const DOCUMENT_INFO_STALE_TIME_MS = 5 * 60_000;
export const DOCUMENT_INFO_TIMEOUT_MS = 30_000;

export const documentInfoOptions = (
    variant: DocumentInfoVariant | undefined
): IDocumentInfoOptions | undefined => (variant ? { variant } : undefined);

export const documentInfoQueryKey = (
    documentId: number | null,
    options?: IDocumentInfoOptions
) =>
    options?.variant
        ? (["document-info", documentId, options.variant] as const)
        : (["document-info", documentId] as const);

/**
 * Get a document's name and presigned viewing URL by ID. With
 * `options.variant` ("report_internal"), read the internal PDF of the
 * completed report whose export has this ID instead; the request sends
 * `?variant=` only when the option is set.
 */
export const getDocumentInfo = async (
    documentId: number,
    options?: IDocumentInfoOptions
): Promise<IDocumentInfo> => {
    const response = await axios.get<IDocumentInfo>(`${API}/${documentId}`, {
        timeout: DOCUMENT_INFO_TIMEOUT_MS,
        ...(options?.variant ? { params: { variant: options.variant } } : {}),
    });
    return response.data;
};

export const documentInfoQueryOptions = (
    documentId: number,
    options?: IDocumentInfoOptions
) => {
    const snapshot = documentInfoOptions(options?.variant);
    return {
        queryKey: documentInfoQueryKey(documentId, snapshot),
        queryFn: () => getDocumentInfo(documentId, snapshot),
        staleTime: DOCUMENT_INFO_STALE_TIME_MS,
    };
};

export const downloadOutputDocument = async (
    documentId: number
): Promise<{ blob: Blob; filename: string }> => {
    let document: IDocumentInfo;
    try {
        document = await getDocumentInfo(documentId);
    } catch (error) {
        if (isAxiosNotFoundError(error)) {
            throw new OutputDocumentMetadataNotFoundError(documentId);
        }
        throw error;
    }
    if (!document.presigned_url) {
        throw new OutputDocumentUnavailableError();
    }
    const response = await storageAxios.get<Blob>(document.presigned_url, {
        responseType: "blob",
        headers: {},
    });
    return {
        blob: response.data,
        filename: document.download_filename ?? document.document_name,
    };
};

// ---------------------------------------------------------------------------
// Extracted Fields
// ---------------------------------------------------------------------------

export const getDocumentExtractedFields = async (
    documentId: number
): Promise<IDocumentExtractedFieldsResponse> => {
    const response = await axios.get<IDocumentExtractedFieldsResponse>(
        `${API}/${documentId}/extracted-fields`
    );
    return response.data;
};

// ---------------------------------------------------------------------------
// Extracted Properties
// ---------------------------------------------------------------------------

export const updateDocumentExtractedProperty = async (
    documentId: number,
    propId: number,
    data: IDocumentExtractedPropertyUpdate
): Promise<IExtractedPropertyInDB> => {
    const response = await axios.put<IExtractedPropertyInDB>(
        `${API}/${documentId}/extracted-properties/${propId}`,
        data
    );
    return response.data;
};

export const deleteDocumentExtractedProperty = async (
    documentId: number,
    propId: number
): Promise<void> => {
    await axios.delete(`${API}/${documentId}/extracted-properties/${propId}`);
};

export const deleteDocumentExtractedPropertiesBatch = async (
    documentId: number,
    fieldDefinitionId: number,
    arrayIndex: number,
    expectedDeleteFingerprint: string
): Promise<number> => {
    const response = await axios.delete<number>(
        `${API}/${documentId}/extracted-properties`,
        {
            params: {
                field_definition_id: fieldDefinitionId,
                array_index: arrayIndex,
                expected_delete_fingerprint: expectedDeleteFingerprint,
            },
        }
    );
    return response.data;
};

// ---------------------------------------------------------------------------
// Highlight Areas (lazy per-property)
// ---------------------------------------------------------------------------

/**
 * Lazy resolution of per-property line-level highlight areas.
 * Called once on PDF source modal open, never on the polling cycle.
 * `highlight_areas` is null when the property has no `chunk_ids`.
 */
export const getExtractedPropertyHighlightAreas = async (
    documentId: number,
    propertyId: number
): Promise<IExtractedPropertyHighlightAreas> => {
    const response = await axios.get<IExtractedPropertyHighlightAreas>(
        `${API}/${documentId}/extracted-properties/${propertyId}/highlight-areas`
    );
    return response.data;
};

// ---------------------------------------------------------------------------
// Custom Fields
// ---------------------------------------------------------------------------

export const createDocumentCustomField = async (
    documentId: number,
    data: IDocumentCustomFieldCreate
): Promise<ICustomFieldResponse> => {
    const response = await axios.post<ICustomFieldResponse>(
        `${API}/${documentId}/custom-fields`,
        data
    );
    return response.data;
};

export const addDocumentCustomFieldProperty = async (
    documentId: number,
    customFieldId: number,
    data: IDocumentCustomFieldPropertyCreate
): Promise<ICustomFieldResponse> => {
    const response = await axios.post<ICustomFieldResponse>(
        `${API}/${documentId}/custom-fields/${customFieldId}/properties`,
        data
    );
    return response.data;
};

export const updateDocumentCustomFieldProperty = async (
    documentId: number,
    propertyId: number,
    data: { value?: string | null; page_number?: number | null }
): Promise<ICustomFieldPropertyResponse> => {
    const response = await axios.put<ICustomFieldPropertyResponse>(
        `${API}/${documentId}/custom-field-properties/${propertyId}`,
        data
    );
    return response.data;
};

export const deleteDocumentCustomFieldProperty = async (
    documentId: number,
    propertyId: number
): Promise<void> => {
    await axios.delete(
        `${API}/${documentId}/custom-field-properties/${propertyId}`
    );
};

export const updateDocumentCustomField = async (
    documentId: number,
    customFieldId: number,
    data: IDocumentCustomFieldUpdate
): Promise<ICustomFieldResponse> => {
    const response = await axios.put<ICustomFieldResponse>(
        `${API}/${documentId}/custom-fields/${customFieldId}`,
        data
    );
    return response.data;
};

export const deleteDocumentCustomField = async (
    documentId: number,
    customFieldId: number
): Promise<void> => {
    await axios.delete(`${API}/${documentId}/custom-fields/${customFieldId}`);
};

// ---------------------------------------------------------------------------
// Certificate of Insurance (COI)
// ---------------------------------------------------------------------------

export const getDocumentCoi = async (
    documentId: number
): Promise<IExtractedCOIResponse | null> => {
    try {
        const response = await axios.get<IExtractedCOIResponse>(
            `${API}/${documentId}/coi`
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export const updateDocumentCoi = async (
    documentId: number,
    data: IExtractedCOIUpdate
): Promise<IExtractedCOIResponse> => {
    const response = await axios.put<IExtractedCOIResponse>(
        `${API}/${documentId}/coi`,
        data
    );
    return response.data;
};

export const deleteDocumentCoi = async (documentId: number): Promise<void> => {
    await axios.delete(`${API}/${documentId}/coi`);
};

// ---------------------------------------------------------------------------
// Extraction Trigger
// ---------------------------------------------------------------------------

export const triggerDocumentExtraction = async (
    documentId: number
): Promise<IExtractionTriggerResponse> => {
    const response = await axios.post<IExtractionTriggerResponse>(
        `${API}/${documentId}/extract`
    );
    return response.data;
};

/**
 * GET the admin document-export xlsx; returns `{ blob, filename }` with
 * `filename` parsed from `Content-Disposition` (defensive fallback to
 * `document-export-${documentId}.xlsx` for proxies that strip the header).
 */
export const downloadDocumentExport = async (
    documentId: number
): Promise<{ blob: Blob; filename: string }> => {
    const response = await axios.get<Blob>(
        `${API}/${documentId}/document-export`,
        {
            responseType: "blob",
        }
    );
    const filename =
        parseContentDispositionFilename(
            response.headers["content-disposition"]
        ) ?? `document-export-${documentId}.xlsx`;
    return { blob: response.data, filename };
};
